import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { FileText, Eye, Send, CheckCircle, XCircle, Download } from 'lucide-react';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import jsPDF from 'jspdf';

interface TaskItem {
  id: string;
  title: string;
  type: 'broker_agreement' | 'rate_confirmation';
  file_url: string;
  status: string;
  created_at: string;
  broker_name?: string;
  company_code: string;
  signed_at?: string;
  signed_by_name?: string;
  load_number?: string;
  delivery_status?: string;
  pod_file_url?: string;
 delivered_at?: string;
  signed_copy_url?: string;
}

const CarrierTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | 'agreements' | 'ratecons'>('all');
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [signingId, setSigningId] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [signing, setSigning] = useState(false);
  const [podUploadId, setPodUploadId] = useState<string | null>(null);
  const [podFile, setPodFile] = useState<File | null>(null);
  const [uploadingPod, setUploadingPod] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchCarrierName();
  }, []);

  const fetchCarrierName = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;
    const { data } = await supabase.from('profiles').select('first_name, last_name').eq('id', session.user.id).single();
    if (data) {
      const name = `${data.first_name} ${data.last_name}`;
      setCarrierName(name);
      setSignatureName(name);
    }
  };

  const fetchTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const { data: apps } = await supabase.from('applications').select('company_code').eq('user_id', session.user.id);
      if (!apps || apps.length === 0) { setTasks([]); setLoading(false); return; }
      const companyCodes = apps.map(a => a.company_code);
      const { data: agreements, error } = await supabase.from('agreements').select('*').in('company_code', companyCodes).or(`carrier_id.eq.${session.user.id},carrier_id.is.null`).order('created_at', { ascending: false });
      if (error) throw error;
      const uniqueCodes = [...new Set((agreements || []).map(a => a.company_code))];
      const { data: brokerProfiles } = await supabase.from('profiles').select('company_name, company_code').in('company_code', uniqueCodes).eq('role', 'broker');
      const enriched = (agreements || []).map(a => ({ ...a, broker_name: brokerProfiles?.find(b => b.company_code === a.company_code)?.company_name || 'Unknown Broker' }));
      setTasks(enriched);
    } catch (error) { console.error('Error fetching tasks:', error); } finally { setLoading(false); }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const handleView = async (taskId: string, fileUrl: string) => {
    try {
      const { data, error } = await supabase.storage.from('carrier-documents').createSignedUrl(fileUrl, 3600);
      if (error || !data?.signedUrl) { alert('Unable to load document'); return; }
      window.open(data.signedUrl, '_blank');
      setViewedIds(prev => new Set([...prev, taskId]));
    } catch (err) { console.error('Error viewing document:', err); }
  };

  const handleDownload = async (fileUrl: string, title: string) => {
    try {
      const { data, error } = await supabase.storage.from('carrier-documents').createSignedUrl(fileUrl, 3600);
      if (error || !data?.signedUrl) { alert('Unable to download document'); return; }
      const link = document.createElement('a');
      link.href = data.signedUrl; link.download = `${title}.pdf`; link.target = '_blank';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) { console.error('Error downloading:', err); }
  };

  const handleSign = async () => {
    if (!signingId || !signatureName.trim()) return;
    const task = tasks.find(t => t.id === signingId);
    if (!task) return;
    setSigning(true);

    try {
      const now = new Date().toISOString();
      const dateStr = new Date(now).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });

      const { data: signedUrl, error: urlError } = await supabase.storage.from('carrier-documents').createSignedUrl(task.file_url, 60);
      if (urlError || !signedUrl?.signedUrl) throw new Error('Cannot access original document');
      const response = await fetch(signedUrl.signedUrl);
      const originalBytes = await response.arrayBuffer();

      let pdfBlob: Blob;

      try {
        const pdfDoc = await PDFDocument.load(originalBytes);
        const pages = pdfDoc.getPages();
        const lastPage = pages[pages.length - 1];
        const { width, height } = lastPage.getSize();
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

        lastPage.drawText('SIGNED', { x: width / 2 - 80, y: height / 2 - 20, size: 60, font: helveticaBold, color: rgb(0.85, 0.92, 0.85), rotate: degrees(45), opacity: 0.25 });

        const sigPage = pdfDoc.addPage([width, height]);
        sigPage.drawRectangle({ x: 0, y: height - 80, width, height: 80, color: rgb(0.1, 0.3, 0.7) });
        sigPage.drawText('SIGNED ACCEPTANCE', { x: 40, y: height - 45, size: 26, font: helveticaBold, color: rgb(1, 1, 1) });
        sigPage.drawText('LoadDrive Electronic Signature', { x: 40, y: height - 65, size: 11, font: helvetica, color: rgb(0.8, 0.85, 1) });

        let y = height - 120;
        sigPage.drawText('DOCUMENT DETAILS', { x: 40, y, size: 12, font: helveticaBold, color: rgb(0.1, 0.3, 0.7) });
        y -= 8;
        sigPage.drawLine({ start: { x: 40, y }, end: { x: width - 40, y }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
        y -= 25;

        const addField = (label: string, value: string) => {
          sigPage.drawText(label, { x: 40, y, size: 9, font: helveticaBold, color: rgb(0.4, 0.4, 0.4) });
          sigPage.drawText(value, { x: 180, y, size: 10, font: helvetica, color: rgb(0, 0, 0) });
          y -= 22;
        };

        addField('Document Title:', task.title);
        addField('Type:', task.type === 'rate_confirmation' ? 'Rate Confirmation' : 'Broker-Carrier Agreement');
        addField('Broker:', task.broker_name || 'N/A');
        if (task.load_number) addField('Load Number:', task.load_number);
        addField('Document ID:', task.id);
        addField('Sent Date:', formatDate(task.created_at));

        y -= 15;
        sigPage.drawText('SIGNATURE', { x: 40, y, size: 12, font: helveticaBold, color: rgb(0.1, 0.3, 0.7) });
        y -= 8;
        sigPage.drawLine({ start: { x: 40, y }, end: { x: width - 40, y }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
        y -= 30;

        addField('Carrier Name:', signatureName);
        addField('Date / Time:', dateStr);
        addField('Status:', 'ACCEPTED & SIGNED');

        y -= 20;
        sigPage.drawText(signatureName, { x: 50, y: y + 10, size: 32, font: helveticaOblique, color: rgb(0, 0, 0.4) });
        y -= 10;
        sigPage.drawLine({ start: { x: 40, y }, end: { x: 300, y }, thickness: 1.5, color: rgb(0, 0, 0) });
        y -= 12;
        sigPage.drawText('Authorized Signature', { x: 40, y, size: 8, font: helvetica, color: rgb(0.5, 0.5, 0.5) });

        sigPage.drawLine({ start: { x: 40, y: 60 }, end: { x: width - 40, y: 60 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
        sigPage.drawText('This document was electronically signed via LoadDrive.', { x: 40, y: 45, size: 8, font: helvetica, color: rgb(0.5, 0.5, 0.5) });
        sigPage.drawText('This electronic signature is legally binding under the ESIGN Act and UETA.', { x: 40, y: 33, size: 8, font: helvetica, color: rgb(0.5, 0.5, 0.5) });
        sigPage.drawText(`Generated: ${dateStr}`, { x: 40, y: 21, size: 8, font: helvetica, color: rgb(0.5, 0.5, 0.5) });

        const signedPdfBytes = await pdfDoc.save({ useObjectStreams: false });
        pdfBlob = new Blob([signedPdfBytes], { type: 'application/pdf' });

      } catch (pdfError) {
        console.warn('pdf-lib failed, generating receipt instead:', pdfError);
        const doc = new jsPDF();
        const pw = doc.internal.pageSize.getWidth();
        doc.setFillColor(25, 76, 178);
        doc.rect(0, 0, pw, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('SIGNED ACCEPTANCE', 20, 18);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text('LoadDrive Electronic Signature Receipt', 20, 30);
        doc.setTextColor(0, 0, 0);
        let ry = 55;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('DOCUMENT DETAILS', 20, ry); ry += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(20, ry, pw - 20, ry); ry += 12;
        doc.setFontSize(10);
        const addRow = (label: string, value: string) => { doc.setFont('helvetica', 'bold'); doc.text(label, 20, ry); doc.setFont('helvetica', 'normal'); doc.text(value, 80, ry); ry += 10; };
        addRow('Title:', task.title);
        addRow('Type:', task.type === 'rate_confirmation' ? 'Rate Confirmation' : 'Broker-Carrier Agreement');
        addRow('Broker:', task.broker_name || 'N/A');
        if (task.load_number) addRow('Load #:', task.load_number);
        addRow('Doc ID:', task.id);
        addRow('Sent:', formatDate(task.created_at));
        ry += 10;
        doc.setFontSize(12); doc.setFont('helvetica', 'bold');
        doc.text('SIGNATURE', 20, ry); ry += 5;
        doc.line(20, ry, pw - 20, ry); ry += 12;
        doc.setFontSize(10);
        addRow('Carrier:', signatureName);
        addRow('Date:', dateStr);
        addRow('Status:', 'ACCEPTED & SIGNED');
        ry += 10;
        doc.setFont('helvetica', 'italic'); doc.setFontSize(24); doc.setTextColor(0, 0, 100);
        doc.text(signatureName, 20, ry); ry += 5;
        doc.setDrawColor(0, 0, 0); doc.line(20, ry, 120, ry); ry += 6;
        doc.setFontSize(8); doc.setTextColor(100, 100, 100); doc.setFont('helvetica', 'normal');
        doc.text('Authorized Signature', 20, ry); ry += 20;
        doc.line(20, ry, pw - 20, ry); ry += 8;
        doc.text('This document was electronically signed via LoadDrive.', 20, ry); ry += 5;
        doc.text('This electronic signature is legally binding under the ESIGN Act and UETA.', 20, ry);
        pdfBlob = doc.output('blob');
      }

      const fileName = `${task.company_code}/signed/${Date.now()}_signed_${task.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      const { error: uploadError } = await supabase.storage.from('carrier-documents').upload(fileName, pdfBlob, { contentType: 'application/pdf' });
      if (uploadError) throw uploadError;

      const { error } = await supabase.from('agreements').update({ status: 'signed', signed_at: now, signed_by_name: signatureName, signed_copy_url: fileName }).eq('id', signingId);
      if (error) throw error;

      setTasks(prev => prev.map(t => t.id === signingId ? { ...t, status: 'signed', signed_at: now, signed_by_name: signatureName } : t));
      await supabase.from('notifications').insert({
        company_code: task.company_code,
        title: 'Rate Con Signed',
        message: `${signatureName} signed "${task.title}"${task.load_number ? ` (Load #${task.load_number})` : ''}`,
        type: 'signed',
        related_id: signingId,
      });
      setSigningId(null);
    } catch (error) {
      console.error('Error signing:', error);
      alert('Failed to sign document.');
    } finally {
      setSigning(false);
    }
  };


 const handleUploadPod = async () => {
    if (!podUploadId || !podFile) return;
    setUploadingPod(true);
    try {
      const task = tasks.find(t => t.id === podUploadId);
      if (!task) return;
      const now = new Date().toISOString();
      const fileName = `${task.company_code}/pod/${Date.now()}_${podFile.name}`;
      const { error: upErr } = await supabase.storage.from('carrier-documents').upload(fileName, podFile, { contentType: podFile.type });
      if (upErr) throw upErr;
      const { error } = await supabase.from('agreements').update({ pod_file_url: fileName, delivery_status: 'pod_submitted', delivered_at: now }).eq('id', podUploadId);
      if (error) throw error;
      setTasks(prev => prev.map(t => t.id === podUploadId ? { ...t, pod_file_url: fileName, delivery_status: 'pod_submitted', delivered_at: now } : t));
      await supabase.from('notifications').insert({ company_code: task.company_code, title: 'Load Delivered — POD Submitted', message: `${carrierName} delivered "${task.title}"${task.load_number ? ` (Load #${task.load_number})` : ''} and uploaded proof of delivery.`, type: 'pod_submitted' });
      setPodUploadId(null); setPodFile(null);
    } catch (err) { console.error('Error:', err); alert('Failed to upload POD.'); }
    finally { setUploadingPod(false); }
  };

  const handleDecline = async () => {
    if (!decliningId || !declineReason.trim()) return;
    try {
      const { error } = await supabase.from('agreements').update({ status: 'declined', decline_reason: declineReason, signed_by_name: carrierName, signed_at: new Date().toISOString() }).eq('id', decliningId);
      if (error) throw error;
      setTasks(prev => prev.map(t => t.id === decliningId ? { ...t, status: 'declined' } : t));
      const declinedTask = tasks.find(t => t.id === decliningId);
      if (declinedTask) {
        await supabase.from('notifications').insert({
          company_code: declinedTask.company_code,
          title: 'Document Declined',
          message: `${carrierName} declined "${declinedTask.title}": ${declineReason}`,
          type: 'declined',
          related_id: decliningId,
        });
      }
      setDecliningId(null);
      setDeclineReason('');
    } catch (error) {
      console.error('Error declining:', error);
      alert('Failed to decline.');
    }
  };

  const filteredTasks = tasks.filter(t => {
    if (activeFilter === 'agreements') return t.type === 'broker_agreement';
    if (activeFilter === 'ratecons') return t.type === 'rate_confirmation';
    return true;
  });

  const pendingCount = tasks.filter(t => t.status === 'active').length;
  const agreementCount = tasks.filter(t => t.type === 'broker_agreement').length;
  const rateConCount = tasks.filter(t => t.type === 'rate_confirmation').length;

  if (loading) return <div className="text-center py-8">Loading tasks...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
         <h2 className="text-2xl font-bold text-gray-900">Tasks </h2>
          <p className="text-sm text-gray-500 mt-1">Review, sign, and manage documents from your brokers</p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-full">{pendingCount} pending</span>
        )}
      </div>

     <div className="flex border-b">
        <button onClick={() => setActiveFilter('all')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeFilter === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>All ({tasks.length})</button>
        <button onClick={() => setActiveFilter('agreements')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeFilter === 'agreements' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Agreements ({agreementCount})</button>
        <button onClick={() => setActiveFilter('ratecons')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeFilter === 'ratecons' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Rate Confirmations ({rateConCount})</button>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-300" />
          <p className="font-medium">All caught up!</p>
          <p className="text-sm mt-1">No pending tasks right now.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {filteredTasks.map((task) => {
            const hasViewed = viewedIds.has(task.id);
            const isPending = task.status === 'active';
            return (
            <div key={task.id} className={`p-4 border-b last:border-b-0 border-l-4 ${task.delivery_status === 'pod_submitted' ? 'border-emerald-500 bg-emerald-50' : task.status === 'signed' ? 'border-green-500 bg-green-50' : task.status === 'declined' ? 'border-red-500 bg-red-50' : 'border-yellow-500'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {task.type === 'rate_confirmation' ? <Send className="h-5 w-5 text-blue-500" /> : <FileText className="h-5 w-5 text-blue-500" />}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-500">
                        From: {task.broker_name} • {formatDate(task.created_at)}
                        {task.load_number && ` • Load: ${task.load_number}`}
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${task.type === 'rate_confirmation' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                          {task.type === 'rate_confirmation' ? 'Rate Con' : 'Agreement'}
                        </span>
                      </p>
                      {task.status === 'signed' && task.signed_at && (
                        <p className="text-xs text-green-600 mt-1">Signed by {task.signed_by_name} on {formatDateTime(task.signed_at)}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold px-3 py-1.5 rounded-md ${
                      task.delivery_status === 'pod_submitted' ? 'bg-emerald-100 text-emerald-700'
                        : task.status === 'signed' ? 'bg-green-100 text-green-700'
                        : task.status === 'declined' ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {task.delivery_status === 'pod_submitted' ? '📄 Load Delivered — POD Submitted'
                        : task.status === 'signed' ? '✓ Signed & Accepted'
                        : task.status === 'declined' ? '✗ Declined'
                        : '⏳ Awaiting Signature'}
                    </span>
                   <button onClick={() => handleView(task.id, (task as any).signed_copy_url || task.file_url)} className={`flex items-center px-4 py-2 text-sm rounded-md ${hasViewed ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold'}`}>
                      <Eye className="h-4 w-4 mr-1" /> {hasViewed ? 'Viewed ✓' : 'View First'}
                    </button>
                    <button onClick={() => handleDownload(task.file_url, task.title)} className="flex items-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                      <Download className="h-4 w-4 mr-1" /> Download
                    </button>
                   {task.status === 'signed' && task.type === 'rate_confirmation' && (!task.delivery_status || task.delivery_status === 'pending') && (
                      <button onClick={() => setPodUploadId(task.id)} className="flex items-center px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 font-semibold">
                        Upload POD & 🚚 Mark Delivered
                      </button>
                    )}
                    {isPending && (
                      <>
                        <button
                          onClick={() => { if (!hasViewed) { alert('Please view the document before signing.'); return; } setSigningId(task.id); setSignatureName(carrierName); }}
                          disabled={!hasViewed}
                          className={`flex items-center px-4 py-2 text-sm text-white rounded-md ${!hasViewed ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Sign & Confirm
                        </button>
                        <button
                          onClick={() => { if (!hasViewed) { alert('Please view the document before declining.'); return; } setDecliningId(task.id); }}
                          disabled={!hasViewed}
                          className={`flex items-center px-4 py-2 text-sm rounded-md ${!hasViewed ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" /> Decline
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {signingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2 text-green-700">Electronic Signature</h3>
            <p className="text-sm text-gray-600 mb-4">
              By typing your name below, you confirm that you have reviewed the document
              <strong> "{tasks.find(t => t.id === signingId)?.title}"</strong>
              {tasks.find(t => t.id === signingId)?.load_number && (<> (Load #: {tasks.find(t => t.id === signingId)?.load_number})</>)}
              {' '}and agree to its terms. This serves as your legally binding electronic signature.
            </p>
            <div className="bg-gray-50 border rounded p-3 mb-4">
              <p className="text-xs text-gray-500">Date & Time</p>
              <p className="text-sm font-medium">{new Date().toLocaleString()}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type your full legal name *</label>
              <input type="text" value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="Your full legal name" className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg" />
              {signatureName && (
                <div className="mt-2 p-3 border-b-2 border-gray-800">
                  <p className="text-2xl italic text-gray-800" style={{ fontFamily: 'cursive' }}>{signatureName}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSigningId(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleSign} disabled={!signatureName.trim() || signing} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                {signing ? 'Signing...' : 'Sign & Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {podUploadId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
           <h3 className="text-lg font-semibold mb-2 text-orange-700">Upload POD & Mark Delivered</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload a photo or document as proof of delivery for <strong>"{tasks.find(t => t.id === podUploadId)?.title}"</strong>
              {tasks.find(t => t.id === podUploadId)?.load_number && (<> (Load #{tasks.find(t => t.id === podUploadId)?.load_number})</>)}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">POD File (PDF, Image) *</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPodFile(e.target.files?.[0] || null)} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setPodUploadId(null); setPodFile(null); }} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleUploadPod} disabled={!podFile || uploadingPod} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {uploadingPod ? 'Submitting...' : '🚚 Submit POD & Mark Delivered'}
              </button>
            </div>
          </div>
        </div>
      )}

      {decliningId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2 text-red-700">Decline Document</h3>
            <p className="text-sm text-gray-600 mb-4">Please provide a reason for declining. This will be sent to your broker.</p>
            <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Reason for declining..." className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4" rows={3} />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setDecliningId(null); setDeclineReason(''); }} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleDecline} disabled={!declineReason.trim()} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">Confirm Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierTasksPage;